import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, Trash2, MessageSquare } from "lucide-react";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/constants";
import { ChatThread } from "@/components/assistant/ChatThread";

export default function AskLasso() {
  const { cid } = useParams();
  const navigate = useNavigate();
  const [convs, setConvs] = useState([]);
  const [threadKey, setThreadKey] = useState(cid || "new");

  const load = useCallback(() => api.get("/assistant/conversations").then((r) => setConvs(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const open = (id) => {
    setThreadKey(id || `new-${Date.now()}`);
    navigate(id ? `/ask/${id}` : "/ask");
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this conversation?")) return;
    await api.delete(`/assistant/conversations/${id}`);
    if (id === cid) open(null);
    load();
  };

  return (
    <div className="grid h-[calc(100vh-7rem)] gap-8 lg:grid-cols-[260px_1fr]">
      <aside className="hidden min-h-0 flex-col lg:flex" data-testid="ask-history">
        <button onClick={() => open(null)} className="inline-flex items-center justify-center gap-2 rounded-full bg-lasso-ink px-5 py-2.5 text-sm font-semibold text-white hover:bg-lasso-navy" data-testid="ask-new-chat">
          <Plus className="h-4 w-4" /> New chat
        </button>
        <p className="mb-2 mt-8 text-[10px] font-semibold uppercase tracking-[0.24em] text-zinc-400">Past conversations</p>
        <div className="min-h-0 flex-1 space-y-1 overflow-y-auto">
          {convs.length === 0 && <p className="text-sm text-muted-foreground">No conversations yet.</p>}
          {convs.map((c) => (
            <div key={c.id} className={`group flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${c.id === cid ? "bg-white font-semibold" : "text-zinc-600 hover:bg-white"}`} data-testid={`ask-conversation-${c.id}`}>
              <MessageSquare className="h-3.5 w-3.5 shrink-0 text-lasso-deep" />
              <button onClick={() => open(c.id)} className="min-w-0 flex-1 text-left">
                <span className="block truncate">{c.title}</span>
                <span className="block text-[11px] font-normal text-zinc-400">{formatDate(c.updated_at)}</span>
              </button>
              <button onClick={() => remove(c.id)} className="opacity-0 transition-opacity group-hover:opacity-100" data-testid={`ask-conversation-delete-${c.id}`} aria-label="Delete"><Trash2 className="h-3.5 w-3.5 text-zinc-400 hover:text-rose-600" /></button>
            </div>
          ))}
        </div>
      </aside>
      <section className="flex min-h-0 flex-col">
        <div className="mb-6">
          <span className="eyebrow text-lasso-deep">Ask Lasso</span>
          <h1 className="mt-2 font-display text-2xl font-bold tracking-tight" data-testid="page-title">SOP assistant</h1>
        </div>
        <div className="min-h-0 flex-1">
          <ChatThread key={threadKey} conversationId={cid} onCreated={(id) => { navigate(`/ask/${id}`, { replace: true }); load(); }} />
        </div>
      </section>
    </div>
  );
}
